import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import useLockedBody from 'app/utils/useLockedBody'
import React, { memo, useState } from 'react'
import { PlayCircle } from 'react-feather'

export interface Props {
  previewImage?: string
  title?: string
  videoID: string
}

export const Video = memo(function Video({
  previewImage,
  title,
  videoID,
}: Props) {
  const [modalStatus, setModalStatus] = useState(false)
  const [_locked, setLocked] = useLockedBody()

  const stopVideos = function () {
    const videos = document.querySelectorAll('iframe, video')
    Array.prototype.forEach.call(videos, function (video) {
      if (video.tagName.toLowerCase() === 'video') {
        video.pause()
      } else {
        const src = video.src
        video.src = src
      }
    })
  }

  if (!videoID) {
    return null
  }

  return (
    <Container>
      <Wrapper dial={4} row wrap>
        <LeftSide>{title ? <Title>{title}</Title> : null}</LeftSide>
        <RightSide>
          <Preview
            style={{ backgroundImage: `url(${previewImage})` }}
            onClick={() => {
              if (!modalStatus) {
                setModalStatus(true)
                if (window !== undefined && window.innerWidth > 1023) {
                  setLocked(true)
                }
              } else {
                setModalStatus(false)
              }
            }}
          >
            <PlayCircle />
          </Preview>
        </RightSide>
      </Wrapper>
      <Modal className={modalStatus ? 'open' : undefined} dial={5}>
        <ModalClose
          onClick={() => {
            setLocked(false)
            setModalStatus(false)
            stopVideos()
          }}
        />
        <Player
          src={`https://www.youtube.com/embed/${videoID}?fs=0&modestbranding=1&rel=0&showinfo=0`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Modal>
    </Container>
  )
})

const Container = styled.section`
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  padding: 7.5rem 10vw 0;

  @media (max-width: 1199px) {
    padding: 3.75rem 8.454vw 0;
  }
`

const Wrapper = styled(FlexBox)``

const LeftSide = styled.div`
  width: 42%;
  padding-right: 6.944vw;

  @media (max-width: 1023px) {
    width: 100%;
    padding-right: 0;
  }
`

const Title = styled.h2`
  max-width: 390px;
  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 4.375rem;
  font-weight: 200;
  line-height: 5.25rem;

  @media (max-width: 1023px) {
    max-width: none;
    font-size: 2.5rem;
    line-height: 3.5rem;
    margin-bottom: 1.5rem;
    text-align: center;
  }
`

const RightSide = styled.div`
  width: 58%;

  @media (max-width: 1023px) {
    width: 100%;
  }
`

const Preview = styled.div`
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  cursor: pointer;
  padding-bottom: 56%;
  position: relative;
  &:hover {
    &:before {
      opacity: 0.3;
    }
  }
  &:before {
    content: '';
    width: 5rem;
    height: 5rem;
    backface-visibility: hidden;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.variants.neutralLight4};
    position: absolute;
    top: 50%;
    left: 50%;
    opacity: 0;
    transform: translate(-50%, -50%);
    z-index: 2;
    transition: 0.3s ease-in-out;
  }
  svg {
    width: 6rem;
    height: 6rem;
    stroke: ${({ theme }) => theme.colors.variants.neutralLight4};
    stroke-width: 0.3;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    polygon {
      fill: ${({ theme }) => theme.colors.variants.neutralLight4};
      stroke-width: 0;
    }
  }

  @media (max-width: 767px) {
    &:before {
      display: none;
    }
    svg {
      width: 3.75rem;
      height: 3.75rem;
      stroke-width: 0.5;
    }
  }
`

const Modal = styled(FlexBox)`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  opacity: 0;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: -1;
  transition: 0.3s ease-in-out;
  &.open {
    opacity: 1;
    visibility: visible;
    z-index: 9999;
  }
`

const ModalClose = styled.div`
  width: 26px;
  height: 26px;
  cursor: pointer;
  position: fixed;
  top: 2.375rem;
  left: 3.611vw;
  &:hover {
    &:before,
    &:after {
      transform: none;
    }
  }
  &:before,
  &:after {
    content: '';
    width: 36px;
    height: 1px;
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    position: absolute;
    top: 12px;
    right: -6px;
    transition: 0.15s ease-out;
  }
  &:before {
    transform: rotate(45deg);
  }
  &:after {
    transform: rotate(-45deg);
  }

  @media (max-width: 767px) {
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
  }
`

const Player = styled.iframe`
  width: 75vw;
  height: 75vh;
  border: 0;

  @media (max-width: 767px) {
    width: 91.546vw;
    height: 80vh;
  }
`
